import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import InventoryService from '../../services/InventoryService';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  Typography,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Event as EventIcon,
  Inventory as InventoryIcon,
  LocalShipping as LocalShippingIcon,
  Warning as WarningIcon,
  CalendarToday as CalendarTodayIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
// axios will be used when connecting to real API
// import axios from 'axios';

const InventoryDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [inventoryItem, setInventoryItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [usageDialogOpen, setUsageDialogOpen] = useState(false);
  const [usageAmount, setUsageAmount] = useState(1);
  const [usageReason, setUsageReason] = useState('');
  const [usageHistory, setUsageHistory] = useState([]);
  const [usageHistoryDialogOpen, setUsageHistoryDialogOpen] = useState(false);
  
  useEffect(() => {
    const fetchInventoryItemData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await InventoryService.getItemById(parseInt(id));
        const inventoryItem = response.data || response;
        
        // Fetch usage history/transactions for this item
        try {
          const transactionsResponse = await InventoryService.getTransactionsByItemId(parseInt(id));
          const transactions = transactionsResponse.data || transactionsResponse || [];
          setUsageHistory(transactions);
        } catch (transactionError) {
          console.warn('Could not fetch transaction history:', transactionError);
          setUsageHistory([]);
        }
        
        setInventoryItem(inventoryItem);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching inventory item data:', error);
        setError('Failed to load inventory item data. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchInventoryItemData();
  }, [id]);
  
  const handleEditItem = () => {
    navigate(`/inventory/${id}/edit`);
  };
  
  const handleDeleteDialogOpen = () => {
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
  };
  
  const handleDeleteItem = async () => {
    try {
      await InventoryService.deleteItem(parseInt(id));
      
      handleDeleteDialogClose();
      navigate('/inventory');
    } catch (error) {
      console.error('Error deleting inventory item:', error);
      alert('Failed to delete inventory item. Please try again.');
      handleDeleteDialogClose();
    }
  };
  
  const handleUsageDialogOpen = () => {
    setUsageDialogOpen(true);
  };
  
  const handleUsageDialogClose = () => {
    setUsageDialogOpen(false);
    setUsageAmount(1);
    setUsageReason('');
  };
  
  const handleUsageAmountChange = (e) => {
    const value = parseInt(e.target.value);
    if (value > 0 && value <= inventoryItem.quantity) {
      setUsageAmount(value);
    }
  };
  
  const handleUsageReasonChange = (e) => {
    setUsageReason(e.target.value);
  };
  
  const handleRecordUsage = async () => {
    try {
      // Validate input
      if (usageAmount <= 0 || usageAmount > inventoryItem.quantity) {
        alert('Please enter a valid usage amount');
        return;
      }
      
      if (!usageReason.trim()) {
        alert('Please enter a reason for usage');
        return;
      }
      
      // In a real application, you would call your API to record the usage
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Create a new usage record
      const newUsage = {
        id: usageHistory.length + 1,
        date: new Date().toISOString(),
        amount: usageAmount,
        reason: usageReason,
        user: 'Current User' // In a real app, this would be the logged-in user
      };
      
      // Update the usage history
      setUsageHistory([newUsage, ...usageHistory]);
      
      // Update the inventory item quantity
      setInventoryItem({
        ...inventoryItem,
        quantity: inventoryItem.quantity - usageAmount,
        updatedAt: new Date().toISOString()
      });
      
      handleUsageDialogClose();
    } catch (error) {
      console.error('Error recording usage:', error);
      alert('Failed to record usage. Please try again.');
    }
  };
  
  const handleUsageHistoryDialogOpen = () => {
    setUsageHistoryDialogOpen(true);
  };
  
  const handleUsageHistoryDialogClose = () => {
    setUsageHistoryDialogOpen(false);
  };
  
  const isLowStock = (item) => {
    return item.quantity <= item.reorderLevel;
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6">{error}</Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Box>
    );
  }
  
  if (!inventoryItem) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">Inventory item not found</Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
          component={RouterLink}
          to="/inventory"
        >
          Back to Inventory
        </Button>
      </Box>
    );
  }
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            sx={{ mr: 1 }}
            component={RouterLink}
            to="/inventory"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Inventory Item Details
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RemoveCircleOutlineIcon />}
            sx={{ mr: 1 }}
            onClick={handleUsageDialogOpen}
            color="secondary"
          >
            Record Usage
          </Button>
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            sx={{ mr: 1 }}
            onClick={handleUsageHistoryDialogOpen}
          >
            Usage History
          </Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            sx={{ mr: 1 }}
            onClick={handleEditItem}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteDialogOpen}
          >
            Delete
          </Button>
        </Box>
      </Box>
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">
                  {inventoryItem.name}
                </Typography>
                <Chip 
                  label={inventoryItem.category}
                  color="primary"
                  variant="outlined"
                />
              </Box>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <InventoryIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Quantity
                  </Typography>
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                    {isLowStock(inventoryItem) && (
                      <Tooltip title="Low stock">
                        <WarningIcon color="warning" fontSize="small" sx={{ mr: 1 }} />
                      </Tooltip>
                    )}
                    {inventoryItem.quantity} {inventoryItem.unit}
                    {isLowStock(inventoryItem) && (
                      <Typography variant="caption" color="warning.main" sx={{ ml: 1 }}>
                        (Below reorder level of {inventoryItem.reorderLevel})
                      </Typography>
                    )}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocalShippingIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Supplier
                  </Typography>
                  <Typography variant="body1">
                    {inventoryItem.supplier}
                  </Typography>
                  {inventoryItem.supplierContact && (
                    <Typography variant="caption" color="text.secondary">
                      {inventoryItem.supplierContact}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CalendarTodayIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Last Restocked
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(inventoryItem.lastRestocked)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EventIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Expiry Date
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(inventoryItem.expiryDate)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Item Details
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Location
                    </Typography>
                    <Typography variant="body2">
                      {inventoryItem.location || 'Not specified'}
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Batch Number
                    </Typography>
                    <Typography variant="body2">
                      {inventoryItem.batchNumber || 'Not specified'}
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Cost Per Unit
                    </Typography>
                    <Typography variant="body2">
                      {inventoryItem.costPerUnit ? `$${inventoryItem.costPerUnit.toFixed(2)}` : 'Not specified'}
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Total Value
                    </Typography>
                    <Typography variant="body2">
                      {inventoryItem.costPerUnit ? `$${(inventoryItem.quantity * inventoryItem.costPerUnit).toFixed(2)}` : 'Not specified'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
            
            {(inventoryItem.description || inventoryItem.notes) && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                
                {inventoryItem.description && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Description
                    </Typography>
                    <Typography variant="body1" paragraph>
                      {inventoryItem.description}
                    </Typography>
                  </Box>
                )}
                
                {inventoryItem.notes && (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      Notes
                    </Typography>
                    <Typography variant="body1" paragraph>
                      {inventoryItem.notes}
                    </Typography>
                  </Box>
                )}
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Created: {new Date(inventoryItem.createdAt).toLocaleString()}
          {inventoryItem.updatedAt !== inventoryItem.createdAt && 
            ` | Last updated: ${new Date(inventoryItem.updatedAt).toLocaleString()}`}
        </Typography>
      </Box>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
      >
        <DialogTitle>Delete Inventory Item</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this inventory item? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancel</Button>
          <Button onClick={handleDeleteItem} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Record Usage Dialog */}
      <Dialog
        open={usageDialogOpen}
        onClose={handleUsageDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Record Item Usage</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Record the usage of {inventoryItem?.name}. Current stock: {inventoryItem?.quantity} {inventoryItem?.unit}.
          </DialogContentText>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Amount Used"
                type="number"
                fullWidth
                value={usageAmount}
                onChange={handleUsageAmountChange}
                inputProps={{ min: 1, max: inventoryItem?.quantity }}
                helperText={`Maximum available: ${inventoryItem?.quantity} ${inventoryItem?.unit}`}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Reason for Usage"
                fullWidth
                multiline
                rows={3}
                value={usageReason}
                onChange={handleUsageReasonChange}
                placeholder="e.g., Dispensed to patient, Damaged, Expired, etc."
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUsageDialogClose}>Cancel</Button>
          <Button 
            onClick={handleRecordUsage} 
            color="primary" 
            variant="contained" 
            disabled={usageAmount <= 0 || usageAmount > inventoryItem?.quantity || !usageReason.trim()}
          >
            Record Usage
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Usage History Dialog */}
      <Dialog
        open={usageHistoryDialogOpen}
        onClose={handleUsageHistoryDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Usage History - {inventoryItem?.name}</DialogTitle>
        <DialogContent>
          {usageHistory.length === 0 ? (
            <Typography variant="body1" sx={{ py: 2 }}>
              No usage history available for this item.
            </Typography>
          ) : (
            <Box sx={{ mt: 2 }}>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Recorded By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usageHistory.map((usage) => (
                      <TableRow key={usage.id}>
                        <TableCell>{new Date(usage.date).toLocaleString()}</TableCell>
                        <TableCell align="right">{usage.amount} {inventoryItem?.unit}</TableCell>
                        <TableCell>{usage.reason}</TableCell>
                        <TableCell>{usage.user}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUsageHistoryDialogClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryDetails;
